import { Spinner } from '@maternity/mun-spinner';
import * as React from 'react';

import type { paths } from '../schemas/signup';

export type SignRequestResponse =
  paths['/sign_request']['post']['responses'][200]['content']['application/json'];

export type SessionEndType =
  | 'signing_complete' // The recipient has signed the document.
  | 'cancel' // The recipient decided to finish later.
  | 'decline' // The recipient declined to sign the document.
  | 'exception' // An exception has occurred on the server during the signing session.
  | 'fax_pending' // Recipient has a fax pending.
  | 'session_timeout' // The recipient did not sign the document in time.
  | 'ttl_expired' // The token was not used within the timeout period or the token has already been accessed.
  | 'viewing_complete'; // The recipient did not need to sign but has completed the viewing ceremony.

export interface DocuSignEvent {
  sessionEndType: SessionEndType;
}

export const EmbeddedDocuSign = ({
  signRequestResponse,
  onSessionEnd,
}: {
  signRequestResponse: SignRequestResponse | undefined;
  onSessionEnd: (event: DocuSignEvent) => void;
}) => {
  // See DocuSign docs:
  // https://developers.docusign.com/docs/esign-rest-api/esign101/concepts/embedding/
  // https://developers.docusign.com/docs/esign-rest-api/how-to/request-signature-focused-view/
  // https://developers.docusign.com/docs/esign-rest-api/reference/envelopes/envelopeviews/createrecipient/
  // https://www.docusign.com/blog/developers/15-minutes-to-better-ux-enhancing-embedded-signing-focused-view
  // https://github.com/docusign/sample-app-myhealthcare-python
  React.useEffect(() => {
    if (!signRequestResponse) {
      return;
    }
    (window as any).DocuSign.loadDocuSign(signRequestResponse.client_id).then(
      (docusign: any) => {
        const signing = docusign.signing({
          url: signRequestResponse.view_url,
          displayFormat: 'focused',
        });
        signing.on('sessionEnd', onSessionEnd);
        signing.mount('#agreement');
      },
    );
  }, [signRequestResponse, onSessionEnd]);

  if (!signRequestResponse) {
    return <Spinner />;
  }

  return <div id="agreement" aria-label="DocuSign Embedded Document" />;
};
